import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { ringGeometry } from '../../utils/progressRing';
import { PROGRESS_ACCENT_TEXT_CLASSES, type ProgressAccent } from './progressAccents';

export interface ProgressRingProps extends HTMLAttributes<HTMLDivElement> {
    /** 0..1, clamped. Caller-driven — no internal timer (use `useNow` to tick it). */
    value: number;
    radius?: number;
    strokeWidth?: number;
    accent?: ProgressAccent;
    /** Centre content, e.g. a countdown built from `formatCountdown`/`formatDuration`. */
    children?: ReactNode;
}

/**
 * The upgrade countdown ring. Geometry lives in the pure
 * `src/utils/progressRing.ts` helper so it stays unit-testable and portable.
 */
export const ProgressRing = forwardRef<HTMLDivElement, ProgressRingProps>(function ProgressRing(
    { value, radius = 27, strokeWidth = 4, accent = 'zone', children, className, ...rest },
    ref,
) {
    const { circumference, dashOffset, size, center } = ringGeometry(value, radius, strokeWidth);

    return (
        <div
            ref={ref}
            className={cn('relative inline-flex items-center justify-center', className)}
            style={{ width: size, height: size }}
            {...rest}
        >
            <svg width={size} height={size} className="-rotate-90" viewBox={`0 0 ${size} ${size}`}>
                <circle cx={center} cy={center} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-space-700" />
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    className={PROGRESS_ACCENT_TEXT_CLASSES[accent]}
                />
            </svg>
            {children && <div className="absolute inset-0 flex items-center justify-center">{children}</div>}
        </div>
    );
});

export default ProgressRing;
